import { createTheme } from '@mui/material/styles';
import type { ThemeMode } from '@/store/slices/uiSlice';

export function createAppTheme(mode: ThemeMode) {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: '#3b78d7',
        light: '#7aa6e6',
        dark: '#2a5ba8',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#62559a',
        light: '#8d82b8',
        dark: '#49407a',
        contrastText: '#ffffff',
      },
      success: {
        main: '#1b9e5e',
        light: '#e3f5ec',
        dark: '#14794a',
      },
      warning: {
        main: '#f5a623',
        light: '#fef3e0',
        dark: '#b8780e',
      },
      error: {
        main: '#e84855',
        light: '#fce8ea',
        dark: '#c4303d',
      },
      info: {
        main: '#3b78d7',
        light: '#e0ecfa',
        dark: '#2a5ba8',
      },
      background: {
        default: isDark ? '#0f1923' : '#f4f6f9',
        paper: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
      },
      text: {
        primary: isDark ? '#f4f6f9' : '#0f1923',
        secondary: isDark ? '#8fa3b8' : '#5a6d82',
        disabled: isDark ? '#5a6d82' : '#8fa3b8',
      },
      divider: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
    },
    typography: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 600,
      h1: { fontWeight: 600 },
      h2: { fontWeight: 600 },
      h3: { fontWeight: 600 },
      h4: { fontWeight: 600 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      body1: { fontWeight: 400 },
      body2: { fontWeight: 400 },
      subtitle1: { fontWeight: 500 },
      subtitle2: { fontWeight: 500 },
      caption: { fontWeight: 500 },
      overline: { fontWeight: 500 },
      button: { fontWeight: 500 },
    },
    shape: {
      borderRadius: 10,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 10,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
            backdropFilter: 'blur(12px)',
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
            backdropFilter: 'blur(12px)',
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
            borderRadius: 14,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            borderRadius: 6,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#1d2d50' : '#ffffff',
            backdropFilter: 'blur(12px)',
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
            borderRadius: 14,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 10,
            },
          },
        },
      },
    },
  });
}
